/**
 * 日照（§3.8 / §10.6）。
 *
 * 時間帯で日の当たる位置が動く。同じ場所の価値が朝と夕方で変わる。
 * ただし**締切にはしない**（§2.4）。日が陰っても何も失われないし、
 * 何かが間に合わなくなることも無い。ただ「変化」だけがある。
 *
 * 実時刻には連動しない。ゲーム内時計で動くので、速度変更（2倍/4倍）で
 * 日も速く動く。閉じている間は世界ごと止まる（§10.5）。
 */
import { GameState } from './state';

/** 1日の長さ（シム内秒）。等速で4分、4倍速なら1分 */
export const DAY_SEC = 240;

export type Phase = 'dawn' | 'day' | 'dusk' | 'night';

export interface SunState {
  /** 0..1 の一日の進み具合 */
  t: number;
  phase: Phase;
  /** 太陽の高さ。0（地平）〜1（真上）。夜は0 */
  height: number;
  /** 日が当たっている帯。夜は null */
  band: { row: number; c0: number; c1: number } | null;
}

export function dayFraction(state: GameState): number {
  const t = (state.time % DAY_SEC) / DAY_SEC;
  return t < 0 ? t + 1 : t;
}

/**
 * 光は右側の窓から入る。
 * 昼は高い角度なので浅くしか届かず（窓際だけ）、
 * 朝夕は低い角度なので奥まで差し込む。同じ場所の価値が時間で変わる。
 */
export function sunAt(state: GameState): SunState {
  const t = dayFraction(state);
  const row = state.sun.row;

  // 0.0 夜明け / 0.25 朝 / 0.5 昼 / 0.75 夕 / 1.0 夜
  const daylight = Math.sin(Math.PI * Math.min(1, Math.max(0, (t - 0.08) / 0.84)));
  const height = t < 0.08 || t > 0.92 ? 0 : daylight;

  let phase: Phase = 'night';
  if (t >= 0.08 && t < 0.22) phase = 'dawn';
  else if (t >= 0.22 && t < 0.7) phase = 'day';
  else if (t >= 0.7 && t <= 0.92) phase = 'dusk';

  if (height <= 0.02) return { t, phase, height, band: null };

  // 低い日ほど奥（左）まで届く
  const depth = Math.round(3 + (1 - height) * 10);
  const c1 = state.sun.c1;
  const c0 = Math.max(1, c1 - depth);
  return { t, phase, height, band: { row, c0, c1 } };
}

/** そのマスに今、日が当たっているか */
export function isSunny(state: GameState, c: number, r: number): boolean {
  const sun = sunAt(state);
  if (!sun.band) return false;
  return r === sun.band.row && c >= sun.band.c0 && c <= sun.band.c1;
}
